import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Redirect } from "react-router-dom";
import { getSelectedHotel, setSelectedLanding } from "../../actions/hotel";
import { getRoom } from "../../modules/rooms/store";
import { useHotel } from "../../graphql/useHotels";
import Landing from "../../views/Landing";
import ErrorMessage from "../ErrorMessage";
import Loading from "../Loading";

const CheckOutSuccess = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const project = useSelector(getSelectedHotel);
    const room = useSelector(getRoom);
    const selectedHotelRef = project?.ref;
    const selectedLanding = useSelector((state) =>
        selectedHotelRef && state.session.selectedLandings ? state.session.selectedLandings[selectedHotelRef] : null,
    );
    const { hotel: loadHotel, loading: loadingHotel, error: errorHotel, data: hotelInfo } = useHotel();

    const hotelData = hotelInfo?.getProject || null;
    const expressCheckoutRef =
        location?.state?.expressCheckoutRef || hotelData?.expressCheckout?.ref || selectedLanding?.expressCheckoutRef;

    useEffect(() => {
        if (!selectedHotelRef) return;

        const variables = { ref: selectedHotelRef };
        if (room?.number) {
            variables.roomNumber = room.number;
        }
        if (room?.code) {
            variables.password = room.code;
        }
        loadHotel({ variables });
    }, [selectedHotelRef, room?.number, room?.code]); // eslint-disable-line

    useEffect(() => {
        if (!hotelData || !expressCheckoutRef) return;

        const parseJson = (data) => {
            try {
                return data ? JSON.parse(data) : null;
            } catch (error) {
                console.error(error);
                return null;
            }
        };

        const baseLanding = selectedLanding || {
            mainLandingRef: hotelData?.landing?.ref || expressCheckoutRef,
            landingRef: hotelData?.landing?.ref || expressCheckoutRef,
            designRef: hotelData?.designRef,
            history: [],
            theme: hotelData?.theme,
            salesStyles: parseJson(hotelData?.sales),
            mapsStyles: parseJson(hotelData?.maps),
            vodDomain: hotelData?.VODDomain,
            landingStyle: hotelData?.landing?.style || null,
            googleApiKey: hotelData?.mapsGoogle?.apiKey ?? "",
        };
        const latestLandingStyle = hotelData?.landing?.style ?? null;
        const landingStyleChanged =
            JSON.stringify(baseLanding?.landingStyle || null) !== JSON.stringify(latestLandingStyle);

        if (
            String(baseLanding?.landingRef || "") === String(expressCheckoutRef) &&
            String(baseLanding?.expressCheckoutRef || "") === String(expressCheckoutRef) &&
            !landingStyleChanged
        ) {
            return;
        }

        const nextLanding = {
            ...baseLanding,
            expressCheckoutRef: hotelData?.expressCheckout?.ref ?? baseLanding?.expressCheckoutRef ?? null,
            mainLandingRef: baseLanding?.mainLandingRef || hotelData?.landing?.ref || expressCheckoutRef,
            designRef: baseLanding?.designRef || hotelData?.designRef,
            theme: baseLanding?.theme || hotelData?.theme,
            salesStyles: baseLanding?.salesStyles || parseJson(hotelData?.sales),
            mapsStyles: baseLanding?.mapsStyles || parseJson(hotelData?.maps),
            vodDomain: baseLanding?.vodDomain || hotelData?.VODDomain,
            landingStyle: latestLandingStyle,
            googleApiKey: baseLanding?.googleApiKey ?? hotelData?.mapsGoogle?.apiKey ?? "",
            history: [...(baseLanding?.history || [])],
            landingRef: expressCheckoutRef,
        };

        if (baseLanding?.landingRef && String(baseLanding.landingRef) !== String(expressCheckoutRef)) {
            nextLanding.history.push(baseLanding.landingRef);
        }

        dispatch(setSelectedLanding(nextLanding));
    }, [dispatch, hotelData, expressCheckoutRef, selectedLanding]);

    if (!selectedHotelRef) {
        return <Redirect to="/site/list" />;
    }

    if (errorHotel) {
        return <ErrorMessage type="server" error={errorHotel} />;
    }

    if (loadingHotel) {
        return <Loading />;
    }

    if (!hotelData) {
        return <ErrorMessage type="server" />;
    }

    if (!expressCheckoutRef) {
        return <ErrorMessage type="server" />;
    }

    if (!selectedLanding || String(selectedLanding?.landingRef || "") !== String(expressCheckoutRef)) {
        return <Loading />;
    }

    return <Landing data={hotelData} hideTopbar />;
};

export default CheckOutSuccess;
